<template>
    <Head :title="'Contact Gina Betbeze – Realtor in St. Johns County Florida'" >
        <link rel="canonical" href="https://www.ginabetbeze.com/contact" />
        <meta name="description" content="Contact Gina Betbeze, St. Johns County Realtor, for expert real estate help. Reach out via phone, email, or form for buying or selling assistance in Florida." />
    </Head>
    <!-- Simple Blurb -->

    <PageContent
        :id="'contact-page'"
        :breadcrumbs="['Home','Contact']"
        :header="'Contact Me'"
    >
        <BasicContainer>
            <FormContainer>
                <form @submit.prevent="submitVelocityForm">
                    <VelocityFormAnimation ref="formAnimation"
                                           :paperTitle="'Contact Form'">
                        <div>
                            <FieldGroup>
                                <div class="w-full text-center">
                                    <h2 id="contact-head" class="text-3xl font-subtitle">
                                        Enter your details, and let's connect to discuss your needs.
                                    </h2>
                                </div>
                            </FieldGroup>
                            <FieldGroup>
                                <div class="w-full tablet-sm:w-1/2">
                                    <Input
                                        v-model="contactForm.firstname"
                                        :name="`firstname`"
                                        :label="'First Name'"
                                        :type="`text`"
                                        required
                                    />
                                    <!-- First Name input would go here -->
                                </div>
                                <div class="w-full tablet-sm:w-1/2">
                                    <Input
                                        v-model="contactForm.lastname"
                                        :name="`lastname`"
                                        :label="'Last Name'"
                                        :type="`text`"
                                        required
                                    />
                                    <!-- Last Name input would go here -->
                                </div>
                            </FieldGroup>

                            <FieldGroup>
                                <div class="w-full tablet-sm:w-1/2">
                                    <Input
                                        v-model="contactForm.email"
                                        :name="`email`"
                                        :label="'Email'"
                                        :type="`email`"
                                        required
                                    />
                                    <!-- Email input would go here -->
                                </div>
                                <div class="w-full tablet-sm:w-1/2">
                                    <Input
                                        v-model="contactForm.phone"
                                        :name="`phone`"
                                        :label="'Phone (Optional)'"
                                        :type="`tel`"
                                    />
                                    <!-- Home Phone input would go here -->
                                </div>
                            </FieldGroup>

                            <FieldGroup>
                                <Input
                                    v-model="contactForm.subject"
                                    :name="`subject`"
                                    :label="'Subject'"
                                    :type="`text`"
                                    required
                                />
                                <!-- Comments textarea would go here -->
                            </FieldGroup>

                            <FieldGroup>
                                <Textarea v-model="contactForm.body" :name="`body`" :label="'Body'" :type="`text`" required />
                                <!-- Comments textarea would go here -->
                            </FieldGroup>

                            <div class="w-full flex justify-center">
                                <Button class="max-w-96">Submit</Button>
                            </div>
                        </div>
                    </VelocityFormAnimation>
                </form>
            </FormContainer>

            <SecondaryHeader>Or send me a communication here:</SecondaryHeader>

            <div class="m-4 tablet-sm:m-8 flex flex-col bg-gray-100 w-full justify-start items-start">
                <p>RE/MAX Specialists</p>
                <p>Gina Betbeze</p>
                <p>12646 San Jose Blvd, Jacksonville, FL 32223</p>
            </div>
        </BasicContainer>
    </PageContent>
</template>
<script setup>
import PageContent from "../Components/Layout/PageContent.vue";
import HomeLayout from "../Layouts/HomeLayout.vue";
import { onMounted, ref } from "vue";
import { Head, Link, useForm, usePage } from "@inertiajs/vue3";
import CallToAction from "../Components/PageContent/CallToAction.vue";
import Textarea from "../Components/Forms/Textarea.vue";
import Input from "../Components/Forms/Input.vue";
import Button from "../Components/Forms/Button.vue";
import Breadcrumbs from "@/Components/PageContent/Breadcrumbs.vue";
import MainHead from "@/Components/PageContent/MainHead.vue";
import SecondaryHeader from "@/Components/PageContent/SecondaryHeader.vue";
import BasicContainer from "@/Components/PageContent/Containers/BasicContainer.vue";
import FormContainer from "@/Components/PageContent/Containers/FormContainer.vue";
import FieldGroup from "@/Components/Forms/FieldGroup.vue";
import VelocityFormAnimation from "@/Components/Forms/VelocityFormAnimation.vue";
import { route } from "ziggy-js";
import { Inertia } from "@inertiajs/inertia";

defineOptions({ layout: HomeLayout });
const page = usePage();
const props = defineProps({
    title: {
        type: String,
        required: true,
    },
    autofill: {
        // This is just the qs data
        type: Object,
        required: false,
    },
});

const formAnimation = ref(null);

const contactForm = useForm({
    firstname: props.autofill?.firstname ?? "",
    lastname: props.autofill?.lastname ?? "",
    email: props.autofill?.email ?? "",
    phone: props.autofill?.phone ?? "",
    subject: props.autofill?.subject ?? "",
    body: props.autofill?.body ?? "",
});

const submitVelocityForm = async (e) => {
    e.preventDefault();
    let formKey = 'general_inquiry';
    let redirectKey = "/contact";
    // Collect the submission data in a json string for insertion into submission table
    const jsonData = JSON.stringify(contactForm.data());

    const velocitySubmissionForm = useForm({
        form_key: formKey,
        field_values: jsonData,
        redirect: redirectKey,
        recaptcha_token: ""
    });

    try {
        // Execute reCAPTCHA
        velocitySubmissionForm.recaptcha_token  = await grecaptcha.enterprise.execute('6LeDhtAqAAAAAJm-0AUDswv8xmB2K8knAmdzyDHi', { action: 'SUBMIT' });
    } catch (error) {
        console.error('reCAPTCHA error:', error);
    }

    velocitySubmissionForm.post( route('velocitySubmission.submitPublic', formKey ),{
        preserveState: true,
        preserveScroll: true,

        onError: (errors) => {
            // Log the detailed error object to the console for debugging
            console.error('Error Details:', errors);

            // Construct a detailed error message if the errors object contains specific error messages
            let detailedErrorMessage = 'There was an error submitting the form "' + page.props.velocityForm.title +
                '".';
            if (errors && typeof errors === 'object' && Object.keys(errors).length > 0) {
                // Append each error message to the detailed error message
                Object.keys(errors).forEach((key) => {
                    detailedErrorMessage += ` ${key}: ${errors[key]}`;
                });
            }
            formAnimation.value.toggleOnEnvelopeFailAnimation(detailedErrorMessage);

            // Display the detailed error message in the UI
            page.props.messages.push({
                message: detailedErrorMessage,
                duration: 10,
                type: "error"
            });
            console.log(detailedErrorMessage);
        },
        onSuccess: () => {
            formAnimation.value.toggleOnEnvelopeSentAnimation();
            setTimeout(() => {
                contactForm.reset();
                Inertia.visit(route(route().current()));
            }, 7500)
        },
    });
};
</script>
